import React, { FC, useMemo, useState, useEffect } from 'react';
import { useGetDeployment } from '../../../api/deployment';
import { CheckResult, ResultStatus } from '../../../types/tecton_proto/configurationcheck/configurationcheck';
import {
  Badge,
  Button,
  ComboFilter,
  ComboFilterCategory,
  ComboFilterListSelector,
  Empty,
  ExtendedTableColumn,
  FlexGroup,
  FlexItem,
  Flyout,
  Input,
  KeyValueList,
  Loading,
  Popover,
  Select,
  Table,
  TableViewLayout,
  Text,
  useTableFilters,
  getFilteredItems,
  tableStringSortParameters,
  TimeSince,
  Link,
} from '@lithos';
import { debounce } from 'lodash';

// Custom hook to update time every minute
const useTimeSince = (date: Date) => {
  const [timeSince, setTimeSince] = useState(() => TimeSince(date));

  useEffect(() => {
    setTimeSince(TimeSince(date));

    const interval = setInterval(() => {
      setTimeSince(TimeSince(date));
    }, 60000);

    return () => clearInterval(interval);
  }, [date]);

  return timeSince;
};

// Component for displaying time since that updates every minute
const TimeSinceCell: FC<{ startTime?: string }> = ({ startTime }) => {
  const timeSince = useTimeSince(new Date(startTime ?? ''));
  return <>{timeSince}</>;
};

const statusBadgeMap: Record<ResultStatus, React.ReactNode> = {
  [ResultStatus.RESULT_STATUS_PASSED]: <Badge color="green" size="s" label={'Passed'} />,
  [ResultStatus.RESULT_STATUS_FAILED]: <Badge color="red" size="s" label={'Failed'} />,
  [ResultStatus.RESULT_STATUS_UNSPECIFIED]: <Badge color="gray" size="s" label="Unspecified" />,
  [ResultStatus.RESULT_STATUS_UNKNOWN]: <Badge color="gray" size="s" label="Unknown" />,
};

const statusStringMap: Record<ResultStatus, string> = {
  [ResultStatus.RESULT_STATUS_PASSED]: 'Passed',
  [ResultStatus.RESULT_STATUS_FAILED]: 'Failed',
  [ResultStatus.RESULT_STATUS_UNSPECIFIED]: 'Unspecified',
  [ResultStatus.RESULT_STATUS_UNKNOWN]: 'Unknown',
};

enum DeploymentScorecardColumnKeys {
  SHORT_NAME,
  STATUS,
  LAST_CHECKED,
  DESCRIPTION,
}

interface DeploymentScorecardFilterState {
  status: string[];
}

const CheckFlyout: FC<{ check: CheckResult; setFlyoutContent: (check: CheckResult | undefined) => void }> = ({
  check,
  setFlyoutContent,
}) => {
  const startTimeSince = useTimeSince(new Date(check.start_time ?? ''));
  const lastPassedSince = check.last_passed_time ? useTimeSince(new Date(check.last_passed_time)) : 'Never';

  const items = [
    {
      title: 'Short Name',
      content: check.short_name,
    },
    {
      title: 'Description',
      content: check.description,
    },
    {
      title: 'Status',
      content: <>{statusBadgeMap[check.status ?? ResultStatus.RESULT_STATUS_UNSPECIFIED]}</>,
    },
    {
      title: 'Last Checked',
      content: startTimeSince,
    },
    {
      title: 'Last Passed',
      content: lastPassedSince,
    },
  ];
  return (
    <Flyout
      content={
        <>
          <FlexGroup direction="column" gapSize="8">
            <FlexItem grow="0" shrink="0">
              <Text element="h4">Information</Text>
              <KeyValueList items={items} />
            </FlexItem>
            {check.error_message && (
              <FlexItem grow="1" shrink="0">
                <Text element="h4">Message</Text>
                <FlexGroup padding="3">
                  <Text code>{check.error_message}</Text>
                </FlexGroup>
              </FlexItem>
            )}
          </FlexGroup>
        </>
      }
      title={<>Check Details</>}
      isOpen={true}
      setIsOpen={() => {
        setFlyoutContent(undefined);
      }}
    />
  );
};

const TableSelectors = ({
  hasAnyFilters,
  activeFilterCount,
  handleResetFilters,
  categories,
  onSearchInputChange,
  testId,
}: {
  hasAnyFilters: boolean;
  activeFilterCount: number;
  handleResetFilters: () => void;
  categories: ComboFilterCategory[];
  onSearchInputChange: (query: string) => void;
  testId?: string;
}) => {
  const [selectedCategory, setSelectedCategory] = useState<ComboFilterCategory | undefined>(undefined);
  const [searchQuery, _] = useState<string | undefined>(undefined);
  return (
    <FlexGroup padding="0" alignItems="center">
      <Input
        value={searchQuery}
        onChange={debounce(onSearchInputChange, 300)}
        icon="Search"
        placeholder="Search by name or ID"
        type="string"
        size="s"
        disabled={false}
        testId={testId}
      />
      <ComboFilter
        icon={'Filters'}
        hasAnyActiveFilters={hasAnyFilters}
        activeFilterCount={activeFilterCount}
        resetFilters={handleResetFilters}
        categories={categories}
        setSelectedCategory={setSelectedCategory}
        selectedCategory={selectedCategory}
        testId={testId}
      />
    </FlexGroup>
  );
};

const ColumnsPopover: FC<{ columnsSelector: React.ReactNode }> = ({ columnsSelector }) => {
  const [isColumnsOpen, setIsColumnsOpen] = useState(false);

  return (
    <Popover
      isOpen={isColumnsOpen}
      setIsOpen={setIsColumnsOpen}
      body={columnsSelector}
      trigger={<Button type="secondary" label="Columns" icon="Filters" testId="feature-view-columns-button" />}
    />
  );
};

const DeploymentScorecardTable: FC<{ checks: CheckResult[] }> = ({ checks }) => {
  const defaultFilterState: DeploymentScorecardFilterState = {
    status: [],
  };

  const allValidOptions = useMemo(() => {
    return {
      status: ['Passed', 'Failed', 'Unspecified'],
    };
  }, []);

  const [flyoutContent, setFlyoutContent] = useState<CheckResult | undefined>(undefined);

  const {
    searchQuery,
    hasAnyFilters,
    activeFilterCount,
    sortType,
    setSortType,
    activeHeading,
    setActiveHeading,
    filterState,
    onSearchInputChange,
    handleResetFilters,
    handleFilterChange,
  } = useTableFilters<CheckResult, DeploymentScorecardColumnKeys, DeploymentScorecardFilterState>(defaultFilterState);

  const filterComponents: Record<
    DeploymentScorecardColumnKeys,
    {
      filter: React.ReactNode;
      activeCount: () => number;
      filterCallback: (entity: CheckResult) => boolean;
      name: string;
    }
  > = useMemo(() => {
    return {
      [DeploymentScorecardColumnKeys.SHORT_NAME]: {
        filter: <></>,
        activeCount: () => 0,
        filterCallback: () => true,
        name: 'Short Name',
      },
      [DeploymentScorecardColumnKeys.STATUS]: {
        filter: (
          <ComboFilterListSelector
            selected={filterState['status']}
            setSelected={(status) => {
              handleFilterChange(filterComponents, 'status', status);
            }}
            items={['Passed', 'Failed', 'Unspecified']}
            title={''}
          />
        ),
        activeCount: () => filterState['status'].length,
        filterCallback: () => true,
        name: 'Status',
      },
      [DeploymentScorecardColumnKeys.LAST_CHECKED]: {
        filter: <></>,
        activeCount: () => 0,
        filterCallback: () => true,
        name: 'Last Checked',
      },
      [DeploymentScorecardColumnKeys.DESCRIPTION]: {
        filter: <></>,
        activeCount: () => 0,
        filterCallback: () => true,
        name: 'Description',
      },
    };
  }, [filterState, handleFilterChange]);

  const categories: ComboFilterCategory[] = [
    {
      name: 'Status',
      icon: 'SupportLifeRing',
      resetCallback: () => {
        handleFilterChange(filterComponents, 'status', defaultFilterState.status);
      },
      activeCount: 0,
      popover: filterComponents[DeploymentScorecardColumnKeys.STATUS].filter,
      disabled: allValidOptions['status'].length === 0,
    },
  ];

  const columns: ExtendedTableColumn<CheckResult & { id: string }>[] = [
    {
      name: 'Short Name',
      id: 'short_name',
      content: (check) => {
        return (
          <Link
            name={check.short_name}
            icon={'Book'}
            onClick={() => {
              setFlyoutContent(check);
            }}
          />
        );
      },
      columnWidth: 'm',
      ...tableStringSortParameters(sortType, 'short_name', setSortType),
    },
    {
      name: 'Status',
      content: (check) => {
        return statusBadgeMap[check.status ?? ResultStatus.RESULT_STATUS_UNSPECIFIED];
      },
      columnWidth: 'm',
      filterContent: filterComponents[DeploymentScorecardColumnKeys.STATUS].filter,
      ...tableStringSortParameters(sortType, 'status', setSortType),
    },
    {
      name: 'Last Checked',
      id: 'last_checked',
      content: (check) => {
        return <TimeSinceCell startTime={check.start_time} />;
      },
      columnWidth: 's',
    },
    {
      name: 'Description',
      id: 'description',
      content: (check) => {
        return check.description;
      },
      columnWidth: 'xl',
      ...tableStringSortParameters(sortType, 'description', setSortType),
    },
  ];

  const selectableColumns = columns.map((column) => {
    return { name: column.name as string, visible: true };
  });

  const [columnState, setColumnState] = useState<{ name: string; visible: boolean }[]>(selectableColumns);

  const columnsSelector = (
    <Select
      groups={[
        {
          items: columnState.map((column) => {
            return {
              name: column.name,
              asToggle: true,
              onClick: () => {
                const newColumnState = columnState.map((selectableColumn) => {
                  if (selectableColumn.name !== column.name) {
                    return selectableColumn;
                  }

                  return {
                    name: selectableColumn.name,
                    visible: !selectableColumn.visible,
                  };
                });
                setColumnState && setColumnState(newColumnState);
              },
              selected: column.visible,
              selectable: true,
            };
          }),
        },
      ]}
      filterable={false}
    />
  );

  const filteredColumns = columns
    .map((column) => {
      const matching = columnState.find((stateColumn) => stateColumn.name === column.name);
      if (!matching) {
        return;
      }
      if (matching?.visible !== true) {
        return;
      }

      const asColumn: ExtendedTableColumn<CheckResult & { id: string }> = {
        ...column,
        isActive: column.id === activeHeading,
        setIsActive: (arg) => {
          if (arg) {
            setActiveHeading(column.id as DeploymentScorecardColumnKeys);
            return;
          }
          setActiveHeading(undefined);
        },
        hideCallback: () => {
          const newColumnState = columnState.map((selectableColumn) => {
            if (selectableColumn.name !== column.name) {
              return selectableColumn;
            }

            return {
              name: selectableColumn.name,
              visible: !selectableColumn.visible,
            };
          });

          setColumnState && setColumnState(newColumnState);
        },
        columnsContent: columnsSelector,
      };

      return asColumn;
    })
    .filter((column) => column !== undefined) as ExtendedTableColumn<CheckResult & { id: string }>[];

  const activeFilters = Object.values(filterComponents).filter((filter) => {
    return filter.activeCount() !== 0;
  });

  const filteredResults = getFilteredItems(checks, activeFilters, ['short_name'], searchQuery ?? '', sortType).filter(
    (check) => {
      if (filterState['status'].length > 0) {
        return filterState['status'].includes(statusStringMap[check.status ?? ResultStatus.RESULT_STATUS_UNSPECIFIED]);
      }
      return true;
    }
  );

  return (
    <TableViewLayout
      searchInput={
        <TableSelectors
          hasAnyFilters={hasAnyFilters}
          activeFilterCount={activeFilterCount}
          handleResetFilters={handleResetFilters}
          categories={categories}
          onSearchInputChange={onSearchInputChange}
        />
      }
      filterAndColumnControls={<ColumnsPopover columnsSelector={columnsSelector} />}
      table={
        <>
          <Table
            columns={filteredColumns}
            items={filteredResults.map((check) => ({ ...check, id: check.short_name ?? '' }))}
            // frozenFirstColumn={true}
            emptyProps={
              checks.length === 0
                ? {
                    title: 'No scorecard data available',
                    description:
                      'This cluster may not have deployment data available, or your account may not have access.',
                    placement: 'full-page',
                    actions: [],
                    useCase: 'empty',
                  }
                : {
                    title: 'No matching checks',
                    description: 'Try adjusting your filters or search query.',
                    placement: 'full-page',
                    actions: [],
                    useCase: 'empty',
                  }
            }
            memoized={true}
            frozenFirstColumn={true}
          />
          {flyoutContent && <CheckFlyout check={flyoutContent} setFlyoutContent={setFlyoutContent} />}
        </>
      }
    />
  );
};

const DeploymentScoreCard = () => {
  const { data, isLoading, isError } = useGetDeployment();

  if (isLoading) {
    return <Loading />;
  }

  if (isError) {
    return (
      <Empty
        placement={'full-page'}
        actions={undefined}
        useCase={'error404'}
        title={'Error Loading Deployment Scorecard'}
        description={
          <>
            <Text>
              The Deployment Scorecard requires Administrator-level access. Your account does not have required
              privilieges on this cluster.
            </Text>
            <Text>Please contact your administrator to request access.</Text>
            <Text>If your account does have administrator access, please contact support.</Text>
          </>
        }
      />
    );
  }

  return <DeploymentScorecardTable checks={data?.check_run?.check_results ?? []} />;
};

export default DeploymentScoreCard;
